// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { PreparedBatch, PreparedBatchesResponse } from "@bitebalance/contracts"

/**
 * Prepared Food Inventory view-model (ADR-065), mirrored by
 * mobile/src/lib/preparedView.ts so both clients read the served truth the
 * same way:
 *
 *   - a batch is "cooked" only when the server lists it — the plan
 *     scheduling a batch never means it was prepared;
 *   - the remaining portions come from the server, never from arithmetic
 *     over the plan;
 *   - skipping a meal or eating out changes nothing here, which is exactly
 *     what the copy tells the user.
 */

export type TFn = (key: string, vars?: Record<string, string | number>) => string

/** The prepared batch backing this plan batch, or null when it was never confirmed as cooked. */
export function preparedFor(response: PreparedBatchesResponse | null, batchKey: string): PreparedBatch | null {
  return response?.items.find(b => b.batchKey === batchKey) ?? null
}

/** True when the plan schedules this batch and the user has not confirmed cooking it yet. */
export function isPreparable(response: PreparedBatchesResponse | null, batchKey: string): boolean {
  return (response?.preparable ?? []).some(b => b.batchKey === batchKey)
}

/** The portions the plan schedules for this batch (the confirmation default), or null. */
export function plannedPortionsFor(response: PreparedBatchesResponse | null, batchKey: string): number | null {
  return (response?.preparable ?? []).find(b => b.batchKey === batchKey)?.plannedPortions ?? null
}

/** "3 of 4 portions left", or the all-eaten line once the batch is depleted. */
export function preparedStatusText(batch: PreparedBatch, t: TFn): string {
  if (batch.portionsRemaining <= 0) return t("views.plan.preparedAllEaten", { prepared: trim(batch.portionsPrepared) })
  return t("views.plan.preparedPortionsLeft", { remaining: trim(batch.portionsRemaining), prepared: trim(batch.portionsPrepared) })
}

/** The honest note when the pantry did not cover everything — never a claim that stock existed. */
export function uncoveredText(uncovered: readonly string[], t: TFn): string | null {
  if (uncovered.length === 0) return null
  return t("views.plan.preparedUncovered", { items: uncovered.join(", ") })
}

/** Whole numbers stay whole; halves show one decimal (0.5 portions is a real answer). */
export function trim(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100)
}
